import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Trash2, RotateCcw, CheckSquare, DollarSign, FolderOpen, Loader2, AlertTriangle, Repeat, Package, Car, Store, ChevronDown, ChevronRight, LayoutGrid, List } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useEscapeToHome } from '@/hooks/useKeyboard';

const ICONS = { tasks: CheckSquare, expenses: DollarSign, projects: FolderOpen, habits: Repeat, assets: Package, vehicles: Car, vendors: Store };
const TYPE_MAP = { tasks: 'task', expenses: 'expense', projects: 'project', habits: 'habit', assets: 'asset', vehicles: 'asset', vendors: 'vendor' };
const LABELS = { tasks: 'Tasks', expenses: 'Expenses', projects: 'Workspaces', habits: 'Habits', assets: 'Assets', vehicles: 'Vehicles', vendors: 'Vendors' };

export default function DeletedRecordsPage() {
  const [records, setRecords] = useState({});
  useEscapeToHome();
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [collapsed, setCollapsed] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'board'

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/deleted-records');
      const recs = data.deleted_records || {};
      // Split assets into vehicles and fixed assets
      const vehicles = (recs.assets || []).filter(a => a.asset_type === 'vehicle');
      const fixedAssets = (recs.assets || []).filter(a => a.asset_type !== 'vehicle');
      setRecords({ ...recs, vehicles, assets: fixedAssets });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);
  useEffect(() => { setSelected(new Set()); }, [records]);
  // 'v' toggles view
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'v' && !isTyping) { e.preventDefault(); setViewMode(m => m === 'board' ? 'list' : 'board'); }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, []);

  const restore = async (type, id) => {
    try { await api.post(`/restore/${type}/${id}`); toast.success('Record restored'); fetchDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const permanentDelete = async () => {
    if (!confirmDelete) return;
    if (!window.confirm('This cannot be undone. Are you absolutely sure?')) return;
    try { await api.delete(`/permanent-delete/${confirmDelete.type}/${confirmDelete.id}`); toast.success('Permanently deleted'); setConfirmDelete(null); fetchDeleted(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleSelect = (type, id) => {
    const key = `${type}:${id}`;
    setSelected(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  };

  const selectAllInSection = (key, items) => {
    const type = TYPE_MAP[key];
    const allKeys = items.map(i => `${type}:${i.id}`);
    setSelected(prev => {
      const n = new Set(prev);
      const allSelected = allKeys.every(k => n.has(k));
      allKeys.forEach(k => allSelected ? n.delete(k) : n.add(k));
      return n;
    });
  };

  const toggleCollapse = (key) => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  const bulkRestore = async () => {
    const items = [...selected].map(k => { const [type, id] = k.split(':'); return { type, id }; });
    try { await api.post('/bulk-restore', { items }); toast.success(`${items.length} items restored`); setSelected(new Set()); fetchDeleted(); }
    catch { toast.error('Bulk restore failed'); }
  };

  const bulkPermanentDelete = async () => {
    if (!window.confirm(`Permanently delete ${selected.size} item(s)? This cannot be undone.`)) return;
    const items = [...selected].map(k => { const [type, id] = k.split(':'); return { type, id }; });
    try { await api.post('/bulk-permanent-delete', { items }); toast.success(`${items.length} items permanently deleted`); setSelected(new Set()); fetchDeleted(); }
    catch { toast.error('Bulk delete failed'); }
  };

  const sections = ['tasks', 'expenses', 'habits', 'projects', 'vehicles', 'assets', 'vendors'];
  const allEmpty = sections.every(k => !(records[k]||[]).length);
  const totalItems = sections.reduce((s, k) => s + (records[k]||[]).length, 0);

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="trash-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div><h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Trash</h2><p className="text-sm text-zinc-500 mt-1">{totalItems} deleted item{totalItems !== 1 ? 's' : ''}</p></div>
          <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
            <button onClick={() => setViewMode(viewMode === 'board' ? 'list' : 'board')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'board' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="trash-view-board">
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Board</button>
            <button onClick={() => setViewMode(viewMode === 'list' ? 'board' : 'list')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="trash-view-list">
              <List className="h-3.5 w-3.5" strokeWidth={1.5} /> List</button>
          </div>
        </div>
      </div>
      <div className={`flex-1 overflow-hidden ${viewMode === 'board' ? '' : 'overflow-y-auto p-4 md:px-8'}`}>
        {viewMode === 'board' ? (
          /* Board View - Full Screen like Task Board */
          loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
          : allEmpty ? <div className="flex flex-col items-center justify-center py-20"><Trash2 className="h-12 w-12 text-zinc-300 mb-4" strokeWidth={1} /><p className="text-zinc-500 text-sm">Trash is empty</p></div>
          : <div className="flex gap-3 h-full overflow-x-auto px-4 md:px-6 py-4" data-testid="trash-board-view">
              {sections.map(key => {
                const items = records[key] || [];
                if (items.length === 0) return null;
                const Icon = ICONS[key] || Package;
                const type = TYPE_MAP[key];
                return (
                  <div key={key} className="flex-shrink-0 w-[calc((100vw-3rem)/2)] sm:w-[calc((100vw-4rem)/3)] md:w-0 md:flex-1 md:min-w-[200px] flex flex-col rounded-xl bg-zinc-50 dark:bg-zinc-900/50" data-testid={`trash-board-${key}`}>
                    <div className="flex items-center gap-2 p-3 shrink-0">
                      <Icon className="h-4 w-4 text-zinc-500" />
                      <span className="text-xs font-bold uppercase tracking-wider">{LABELS[key]}</span>
                      <span className="text-xs text-zinc-400 ml-auto">{items.length}</span>
                    </div>
                    <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-2">
                      {items.map(item => (
                        <div key={item.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 group overflow-hidden" data-testid={`trash-board-card-${item.id}`}>
                          <p className="text-sm font-medium mb-1 break-words">{item.title || item.name}</p>
                          {item.deleted_at && <p className="text-[10px] text-zinc-400 mb-2">Deleted {new Date(item.deleted_at).toLocaleDateString()}</p>}
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => restore(type, item.id)}><RotateCcw className="h-2.5 w-2.5 mr-1" /> Restore</Button>
                            <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-red-500" onClick={() => setConfirmDelete({ type, id: item.id, name: item.title || item.name })}><Trash2 className="h-2.5 w-2.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
        ) : (
        <div className="w-full">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
          : allEmpty ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Trash2 className="h-12 w-12 text-zinc-300 mb-4" strokeWidth={1} />
              <p className="text-zinc-500 text-sm">Trash is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sections.map(key => {
                const items = records[key] || [];
                if (items.length === 0) return null;
                const Icon = ICONS[key] || Package;
                const type = TYPE_MAP[key];
                const isCollapsed = collapsed[key];
                const sectionSelected = items.filter(i => selected.has(`${type}:${i.id}`)).length;

                return (
                  <div key={key} className="border border-zinc-200 dark:border-zinc-800 rounded-xl overflow-hidden" data-testid={`trash-section-${key}`}>
                    <button onClick={() => toggleCollapse(key)} className="w-full flex items-center gap-3 p-4 bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors text-left">
                      {isCollapsed ? <ChevronRight className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
                      <Icon className="h-4 w-4 text-zinc-500" />
                      <span className="text-sm font-medium">{LABELS[key] || key}</span>
                      <span className="text-xs text-zinc-400 ml-auto">{items.length}</span>
                    </button>
                    {!isCollapsed && (
                      <div>
                        {/* Select All for section */}
                        <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30">
                          <Checkbox checked={items.length > 0 && items.every(i => selected.has(`${type}:${i.id}`))} onCheckedChange={() => selectAllInSection(key, items)} className="h-3.5 w-3.5" />
                          <span className="text-xs text-zinc-400">Select All ({items.length})</span>
                          {sectionSelected > 0 && <span className="text-xs text-zinc-500 ml-2">{sectionSelected} selected</span>}
                        </div>
                        <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                          {items.map(item => (
                            <div key={item.id} className={`flex items-center justify-between px-4 py-3 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors group ${selected.has(`${type}:${item.id}`) ? 'bg-zinc-50 dark:bg-zinc-800' : ''}`}
                              data-testid={`deleted-item-${item.id}`}>
                              <div className="flex items-center gap-3">
                                <Checkbox checked={selected.has(`${type}:${item.id}`)} onCheckedChange={() => toggleSelect(type, item.id)} className="h-3.5 w-3.5" />
                                <div>
                                  <span className="text-sm font-medium">{item.title || item.name}</span>
                                  {item.deleted_at && <p className="text-[11px] text-zinc-400">Deleted {new Date(item.deleted_at).toLocaleDateString()}</p>}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => restore(type, item.id)}><RotateCcw className="h-3 w-3 mr-1" /> Restore</Button>
                                <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500" onClick={() => setConfirmDelete({ type, id: item.id, name: item.title || item.name })}><Trash2 className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        )}
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 bg-zinc-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50" data-testid="trash-selection-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs underline opacity-70">Clear</button>
          <Button size="sm" variant="secondary" onClick={bulkRestore} className="bg-white/20 hover:bg-white/30 text-white"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore</Button>
          <Button size="sm" variant="destructive" onClick={bulkPermanentDelete}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete Forever</Button>
        </div>
      )}

      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" /> Permanent Delete</DialogTitle>
            <DialogDescription>Are you sure you want to permanently delete "{confirmDelete?.name}"? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={permanentDelete}>Delete Forever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
